import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { apiUrl } from '@/config/api';

interface UnreadCount {
  conversationId: string;
  count: number;
}

export function useUnreadMessages() {
  const { user, token } = useAuth();
  const [unreadCounts, setUnreadCounts] = useState<UnreadCount[]>([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUnreadCounts = async () => {
    if (!user || !token) return;

    try {
      const response = await fetch(apiUrl('/api/chat/conversations/unread'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadCounts(data.unreadCounts || []);
        setTotalUnread(data.totalUnread || 0);
      }
    } catch (_error) {
      console.error('Failed to fetch unread counts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (conversationId: string) => {
    if (!token) return;

    try {
      await fetch(apiUrl('/api/chat/conversations/mark-read'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ conversationId })
      });

      // Update local state
      setUnreadCounts(prev => prev.map(item => 
        item.conversationId === conversationId 
          ? { ...item, count: 0 }
          : item
      ));
      
      setTotalUnread(prev => {
        const conversationCount = unreadCounts.find(item => item.conversationId === conversationId)?.count || 0;
        return Math.max(0, prev - conversationCount);
      });
    } catch (_error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const incrementUnread = (conversationId: string) => {
    setUnreadCounts(prev => {
      const existing = prev.find(item => item.conversationId === conversationId);
      if (existing) {
        return prev.map(item => 
          item.conversationId === conversationId 
            ? { ...item, count: item.count + 1 }
            : item
        );
      } else {
        return [...prev, { conversationId, count: 1 }];
      }
    });
    
    setTotalUnread(prev => prev + 1);
  };

  const decrementUnread = (conversationId: string) => {
    setUnreadCounts(prev => {
      const existing = prev.find(item => item.conversationId === conversationId);
      if (existing && existing.count > 0) {
        return prev.map(item => 
          item.conversationId === conversationId 
            ? { ...item, count: item.count - 1 }
            : item
        );
      }
      return prev;
    });
    
    setTotalUnread(prev => Math.max(0, prev - 1));
  };

  useEffect(() => {
    fetchUnreadCounts();
    
    // Refresh unread counts every 30 seconds
    const interval = setInterval(fetchUnreadCounts, 30000);
    
    return () => clearInterval(interval);
  }, [user, token]);

  return {
    unreadCounts,
    totalUnread,
    isLoading,
    markAsRead,
    incrementUnread,
    decrementUnread,
    refreshUnreadCounts: fetchUnreadCounts
  };
}
